import React, { useEffect, useRef, useState } from 'react';
import LockerLink from '@sls-react/components/Links/LockerLink';
import { themeGet } from '@styled-system/theme-get';
import styled from 'styled-components';
import ValueGuideLink from '@sls-react/components/Links/ValueGuideLink';
import HelpLink from '@sls-react/components/Links/HelpLink';
import LogoutOption from '@sls-react/components/Chrome/Header/Desktop/LogoutOption';
import SettingsPageLink from '@sls-react/components/Links/SettingsPageLink';
import MyPurchasesLink from '@sls-react/components/Links/MyPurchasesLink';
import SellerDashboardLink from '@sls-react/components/Links/SellerDashboardLink';
import { Router } from '@sls-react/routing';
import { Avatar } from '@sls-react/design-system';
import NavActionChild from '@sls-react/components/Chrome/Header/Desktop/NavActionChild';
import { useSession } from '@sls-react/contexts/SessionContext';
import ReferralLink from '@sls-react/components/Links/ReferralLink';
import ReferralDollar from '@sls-react/static/svg/ReferralDollar';
import { useQuery } from 'react-query';
import displayPrice from '@sls-react/utils/displayPrice';
import useV1Session from '@sls-react/hooks/useV1Session';
import { fetchSpendableBalance } from '@sls-react/api/sellerAccount';
import GiftCardLink from '@sls-react/components/Links/GiftCardLink';
import NewTabLink from '@sls-react/components/Links/NewTabLink';
import { useDelayFn } from '@sls-react/hooks/useDelayFn';

const AvatarBlock = styled.div<any>`
  display: flex;
  align-self: flex-end;

  // Margin top to put it in better center align with the rest of the nav
  margin-top: -4px;

  position: relative;
  font-size: 0.75rem;

  ${NavActionChild} {
    right: 0;
    top: 48px;

    .username {
      padding: 4px 8px;
      color: ${themeGet('colors.black')};
    }
  }
`;

const AvatarSection: React.FC = () => {
  const { user } = useSession();
  const [open, setOpen] = useState(false);

  const { data: spendableBalance, status } = useQuery(
    'spendable_balance',
    fetchSpendableBalance,
    { staleTime: 500000, enabled: open },
  );

  const { data: v1Session } = useV1Session();

  const [callDelay, cancel] = useDelayFn(setOpen, 150);

  function handleClose() {
    cancel();
    setOpen(false);
  }

  function closeViaNavigation() {
    handleClose();
  }

  function handleOpen() {
    cancel();
    callDelay(true);
  }

  function handleMouseOut() {
    cancel();
    callDelay(false);
  }

  useEffect(() => {
    Router.events.on('routeChangeStart', closeViaNavigation);
    return () => Router.events.off('routeChangeStart', closeViaNavigation);
  }, []);

  if (!user) {
    return null;
  }

  return (
    <AvatarBlock>
      <button>
        <Avatar
          avatarUrl={v1Session?.user.avatar_url}
          alt="Your avatar"
          onFocus={handleOpen}
          onBlur={handleClose}
          onMouseOver={handleOpen}
          onMouseOut={handleMouseOut}
          aria-controls="avatar-menu"
          aria-label="View account navigation"
          aria-haspopup={true}
          aria-expanded={open ? true : undefined}
        />
      </button>

      <NavActionChild
        onFocus={() => {
          cancel();
          setOpen(true);
        }}
        onBlur={handleClose}
        onMouseOver={() => {
          cancel();
          setOpen(true);
        }}
        onMouseOut={handleMouseOut}
        style={{
          zIndex: 100,
        }}
        className={open ? 'open' : ''}
      >
        <ul role="menu" id="avatar-menu" aria-hidden={!open}>
          <li>
            <LockerLink role="menuitem" username={user.username}>
              <b>@{user.username}</b>
            </LockerLink>
          </li>

          {status !== 'error' && (
            <li>
              <SellerDashboardLink role="menuitem">
                Balance:{' '}
                {spendableBalance != null && (
                  <strong>{displayPrice(spendableBalance.amount)}</strong>
                )}
              </SellerDashboardLink>
            </li>
          )}

          <li>
            <LockerLink role="menuitem" username={user.username}>
              My Locker
            </LockerLink>
          </li>

          <li>
            <GiftCardLink role="menuitem">Gift Cards</GiftCardLink>
          </li>

          <li>
            <ValueGuideLink role="menuitem">Value Guide</ValueGuideLink>
          </li>

          <li>
            <ReferralLink
              page="invite"
              role="menuitem"
              style={{
                display: 'flex',
              }}
            >
              <div className="mr-2">Invite Friends</div>
              <div>
                <ReferralDollar />
              </div>
            </ReferralLink>
          </li>

          <li>
            <SellerDashboardLink passHref role="menuitem">
              Seller Dashboard
            </SellerDashboardLink>
          </li>

          <li>
            <MyPurchasesLink role="menuitem">My Purchases</MyPurchasesLink>
          </li>

          <li>
            <SettingsPageLink page="account" role="menuitem">
              My Settings
            </SettingsPageLink>
          </li>

          <li>
            <SettingsPageLink page="vacation" role="menuitem">
              Vacation Mode
            </SettingsPageLink>
          </li>

          {v1Session?.user?.power_seller && (
            <li>
              <NewTabLink href="https://calendly.com/sidelineswap-emma/30min">
                Schedule a Call
              </NewTabLink>
            </li>
          )}

          <li>
            <HelpLink link="default" role="menuitem">
              Help Center
            </HelpLink>
          </li>

          <LogoutOption />
        </ul>
      </NavActionChild>
    </AvatarBlock>
  );
};

export default AvatarSection;
